
import { Component, computed, effect, inject, OnDestroy, signal, untracked } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import moment from 'moment-timezone';
import { PopupComponent } from '../popup/popup.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { InputComponent } from '../formComponent/input/input.component';
import { DropdownComponent } from '../formComponent/dropdown/dropdown.component';
import { DatePickerNewComponent } from '../formComponent/date-picker-new/date-picker.component';
import { ButtonComponentComponent } from '../button-component/button-component.component';
import { SegmentToggleOption, SegmentToggleSwitchButtonComponent } from '../segment-toggle-switch-button/segment-toggle-switch-button.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { ToastrMessageComponent } from '../formComponent/toastr-message/toastr-message.component';
import { ReminderRowComponent } from '../reminder-row/reminder-row.component';
import { REMIND_AT_FORMAT, reminderRowState, snoozeLabel } from '../reminder-row/reminder-row.util';
import { toContractUtc } from '../../commonFunction/datetime-contract.util';
import { ReminderApis, ReminderListScope, ReminderListState } from '../../../services/taskManagement/reminderApis';
import { UserApis } from '../../../services/userManagement/userApi';
import { UsersService } from '../../../constants/usersDetails';
import { MyRemindersService } from './my-reminders.service';

type ReminderForm = {
  title?: string;
  remind_at?: string;
  recipient_user_ids?: number[];
};

type GroupKey = 'due' | 'snoozed' | 'today' | 'tomorrow' | 'later' | 'dismissed';
type ReminderGroup = { key: GroupKey; label: string; rows: any[] };
type EmptyState = { title: string; message: string };

const PAGE_SIZE = 20;
const GROUP_ORDER: { key: GroupKey; label: string }[] = [
  { key: 'due', label: 'Due now' },
  { key: 'snoozed', label: 'Snoozed' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'later', label: 'Later' },
  { key: 'dismissed', label: 'Dismissed' },
];

@Component({
  selector: 'app-my-reminders',
  standalone: true,
  imports: [
    PopupComponent,
    ConfirmationDialogComponent,
    InputComponent,
    DropdownComponent,
    DatePickerNewComponent,
    ButtonComponentComponent,
    SegmentToggleSwitchButtonComponent,
    EmptyStateComponent,
    ReminderRowComponent
],
  templateUrl: './my-reminders.component.html',
  styleUrl: './my-reminders.component.css',
})
export class MyRemindersComponent implements OnDestroy {
  ui = inject(MyRemindersService);
  private reminderApis = inject(ReminderApis);
  private userApis = inject(UserApis);
  private usersService = inject(UsersService);
  private toastr = inject(ToastrMessageComponent);
  private destroy$ = new Subject<void>();

  readonly scopeSegments: SegmentToggleOption<ReminderListScope>[] = [
    { label: 'For me', value: 'MINE' },
    { label: 'Created by me', value: 'CREATED_BY_ME' },
  ];
  private readonly stateLabels: SegmentToggleOption<ReminderListState>[] = [
    { label: 'Due', value: 'PENDING' },
    { label: 'Upcoming', value: 'UPCOMING' },
    { label: 'Dismissed', value: 'DISMISSED' },
    { label: 'All', value: '' },
  ];

  scope = signal<ReminderListScope>('MINE');
  state = signal<ReminderListState>('PENDING');
  list = signal<any[]>([]);
  dataFilter = signal<any>(null);
  loading = signal<boolean>(false);
  loadingMore = signal<boolean>(false);

  composerOpen = signal<boolean>(false);
  form = signal<ReminderForm>({});
  formError = signal<any>({});
  saving = signal<boolean>(false);
  editing = signal<any>(null);
  usersList = signal<any[]>([]);

  snoozeOpenId = signal<number | null>(null);
  busyId = signal<number | null>(null);
  deleteDialog = signal<{ trigger: boolean; id: number | null; isLoading: boolean }>({ trigger: false, id: null, isLoading: false });
  nowTick = signal<number>(Date.now());
  private nowTimer: any = null;

  me = computed(() => Number(this.usersService.USER?.detail?.id) || 0);
  hasMore = computed(() => {
    const df = this.dataFilter();
    return !!df && df.pageNo < df.totalPages;
  });
  canLoadMore = computed(() => this.hasMore() && !this.loading() && !this.loadingMore());
  totalCount = computed<number | null>(() => {
    const df = this.dataFilter();
    const total = Number(df?.totalCount ?? df?.total ?? NaN);
    return Number.isFinite(total) ? total : null;
  });
  stateSegments = computed<SegmentToggleOption<ReminderListState>[]>(() => {
    const active = this.state();
    const count = this.loading() ? null : this.totalCount();
    return this.stateLabels.map((o) => ({ ...o, count: o.value === active ? count : null }));
  });

  /** Rows bucketed by when they matter, in a fixed order; empty buckets are skipped. */
  groups = computed<ReminderGroup[]>(() => {
    const now = this.nowTick();
    const tz = this.timezone();
    const buckets = new Map<GroupKey, any[]>();
    for (const r of this.list()) {
      const kind = reminderRowState(r, this.me(), now).kind;
      let key: GroupKey;
      if (kind === 'dismissed' || kind === 'snoozed' || kind === 'due') {
        key = kind;
      } else {
        const when = moment.utc(r?.remind_at, REMIND_AT_FORMAT).tz(tz);
        const today = moment(now).tz(tz);
        key = when.isSame(today, 'day') ? 'today' : when.isSame(today.clone().add(1, 'day'), 'day') ? 'tomorrow' : 'later';
      }
      const rows = buckets.get(key) ?? [];
      rows.push(r);
      buckets.set(key, rows);
    }
    return GROUP_ORDER.filter((g) => buckets.has(g.key)).map((g) => ({ ...g, rows: buckets.get(g.key)! }));
  });

  emptyState = computed<EmptyState>(() => {
    switch (this.state()) {
      case 'PENDING':
        return { title: 'Nothing due right now', message: 'Reminders show up here when they come due.' };
      case 'UPCOMING':
        return { title: 'No upcoming reminders', message: 'Set one with "Add reminder" above.' };
      case 'DISMISSED':
        return { title: 'No dismissed reminders yet', message: 'Reminders you dismiss are kept here.' };
      default:
        return { title: 'No reminders yet', message: 'Set one with "Add reminder" above.' };
    }
  });

  private focusHandled: number | null = null;

  constructor() {
    effect(() => {
      if (this.ui.open()) {
        untracked(() => this.onOpen());
      } else {
        untracked(() => this.onClose());
      }
    });
    effect(() => {
      const id = this.ui.focusId();
      if (!id) return;
      untracked(() => {
        if (this.ui.open() && this.focusHandled !== id) this.focusOn(id);
      });
    });
  }

  ngOnDestroy(): void {
    this.stopClock();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private timezone(): string {
    return this.usersService.USER?.timezone || 'UTC';
  }

  private onOpen(): void {
    this.nowTick.set(Date.now());
    this.nowTimer = setInterval(() => this.nowTick.set(Date.now()), 30000);
    this.resetForm();
    this.composerOpen.set(false);
    if (!this.usersList().length) this.loadUsers();
    const focus = this.ui.focusId();
    if (focus) {
      this.focusHandled = focus;
      this.scope.set('MINE');
      this.state.set('PENDING');
    }
    this.load();
  }

  private focusOn(id: number): void {
    this.focusHandled = id;
    this.scope.set('MINE');
    this.state.set('PENDING');
    this.load();
  }

  private applyFocus(): void {
    const id = this.ui.focusId();
    if (!id) return;
    setTimeout(() => document.getElementById('rm-row-' + id)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
    setTimeout(() => {
      if (this.ui.focusId() === id) this.ui.focusId.set(null);
      if (this.focusHandled === id) this.focusHandled = null;
    }, 6000);
  }

  private onClose(): void {
    this.stopClock();
    this.snoozeOpenId.set(null);
    this.editing.set(null);
    this.composerOpen.set(false);
  }

  private stopClock(): void {
    if (this.nowTimer) clearInterval(this.nowTimer);
    this.nowTimer = null;
  }

  close(): void {
    this.ui.open.set(false);
  }

  /* ---------- list ---------- */

  load(): void {
    this.loading.set(true);
    this.snoozeOpenId.set(null);
    this.reminderApis
      .getListData(this.listParams(1))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.list.set(res?.response?.list ?? []);
          this.dataFilter.set(res?.response?.dataFilter ?? null);
          this.loading.set(false);
          this.applyFocus();
        },
        error: (err: any) => {
          this.loading.set(false);
          this.toastr.showError(err?.message || 'Failed to load reminders');
        },
      });
  }

  loadMore(): void {
    if (!this.canLoadMore()) return;
    const next = (this.dataFilter()?.pageNo ?? 1) + 1;
    this.loadingMore.set(true);
    this.reminderApis
      .getListData(this.listParams(next))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.list.update((prev) => [...prev, ...(res?.response?.list ?? [])]);
          this.dataFilter.set(res?.response?.dataFilter ?? null);
          this.loadingMore.set(false);
        },
        error: (err: any) => {
          this.loadingMore.set(false);
          this.toastr.showError(err?.message || 'Failed to load reminders');
        },
      });
  }

  private listParams(pageNo: number): Record<string, any> {
    const params: Record<string, any> = { scope: this.scope(), pageNo, pageSize: PAGE_SIZE };
    if (this.state()) params['state'] = this.state();
    return params;
  }

  setScope(scope: ReminderListScope): void {
    if (this.scope() === scope) return;
    this.scope.set(scope);
    this.load();
  }

  setState(state: ReminderListState): void {
    if (this.state() === state) return;
    this.state.set(state);
    this.load();
  }

  /* ---------- actions ---------- */

  dismiss(r: any): void {
    this.busyId.set(r.id);
    this.reminderApis
      .dismissReminder(r.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId.set(null);
          if (this.editing()?.id === r.id) this.cancelComposer();
          this.toastr.showSuccess('Reminder dismissed');
          this.afterChange();
        },
        error: (err: any) => {
          this.busyId.set(null);
          this.toastr.showError(err?.message || 'Failed to dismiss reminder');
        },
      });
  }

  toggleSnooze(r: any): void {
    this.snoozeOpenId.update((cur) => (cur === r.id ? null : r.id));
  }

  snooze(r: any, minutes: number): void {
    const until = moment.utc().add(minutes, 'minutes').format('YYYY-MM-DD HH:mm');
    this.busyId.set(r.id);
    this.snoozeOpenId.set(null);
    this.reminderApis
      .snoozeReminder(r.id, until)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId.set(null);
          this.toastr.showSuccess(`Snoozed for ${snoozeLabel(minutes)}`);
          this.afterChange();
        },
        error: (err: any) => {
          this.busyId.set(null);
          this.toastr.showError(err?.message || 'Failed to snooze reminder');
        },
      });
  }

  askDelete(r: any): void {
    this.deleteDialog.set({ trigger: true, id: r.id, isLoading: false });
  }

  closeDelete(): void {
    this.deleteDialog.set({ trigger: false, id: null, isLoading: false });
  }

  confirmDelete(): void {
    const id = this.deleteDialog().id;
    if (id == null) return;
    this.deleteDialog.update((d) => ({ ...d, isLoading: true }));
    this.reminderApis
      .deleteReminder(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeDelete();
          if (this.editing()?.id === id) this.cancelComposer();
          this.toastr.showSuccess('Reminder deleted');
          this.afterChange();
        },
        error: (err: any) => {
          this.deleteDialog.update((d) => ({ ...d, isLoading: false }));
          this.toastr.showError(err?.message || 'Failed to delete reminder');
        },
      });
  }

  private afterChange(): void {
    this.load();
    this.ui.notifyChanged();
  }

  /* ---------- composer ---------- */

  openComposer(): void {
    this.resetForm();
    this.composerOpen.set(true);
  }

  cancelComposer(): void {
    this.resetForm();
    this.composerOpen.set(false);
  }

  handleFormChange(event: any): void {
    const { name, value } = event;
    this.form.update((prev) => ({ ...prev, [name]: value }));
    this.formError.update((prev) => ({ ...prev, [name]: null }));
  }

  handleRecipientsChange(event: any): void {
    const ids = (event?.value ?? []).map((v: any) => Number(v)).filter((v: number) => Number.isFinite(v));
    this.form.update((prev) => ({ ...prev, recipient_user_ids: ids }));
    this.formError.update((prev) => ({ ...prev, recipient_user_ids: null }));
  }

  startEdit(r: any): void {
    this.editing.set(r);
    this.form.set({
      title: r?.title ?? '',
      remind_at: r?.remind_at ?? '',
      recipient_user_ids: (r?.recipients ?? []).map((itm: any) => Number(itm.user_id)),
    });
    this.formError.set({});
    this.snoozeOpenId.set(null);
    this.composerOpen.set(true);
  }

  resetForm(): void {
    this.editing.set(null);
    this.form.set({ recipient_user_ids: this.me() ? [this.me()] : [] });
    this.formError.set({});
  }

  submit(): void {
    const f = this.form();
    const errors: any = {};
    if (!String(f.title ?? '').trim()) errors.title = 'Title is required';
    const remindAt = toContractUtc(f.remind_at);
    if (!remindAt) errors.remind_at = 'Remind at is required';
    if (!f.recipient_user_ids?.length) errors.recipient_user_ids = 'Pick at least one recipient';
    if (Object.keys(errors).length) {
      this.formError.set(errors);
      return;
    }

    const body = {
      title: String(f.title).trim(),
      remind_at: remindAt as string,
      recipient_user_ids: f.recipient_user_ids,
    };
    const editing = this.editing();
    const request = editing ? this.reminderApis.updateReminder(editing.id, body) : this.reminderApis.addReminder(body);

    this.saving.set(true);
    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        this.toastr.showSuccess(res?.message || (editing ? 'Reminder updated' : 'Reminder added'));
        this.cancelComposer();
        this.afterChange();
      },
      error: (err: any) => {
        this.saving.set(false);
        this.formError.set(err?.response?.field_errors || {});
        this.toastr.showError(err?.message || 'Failed to save reminder');
      },
    });
  }

  private loadUsers(): void {
    this.userApis
      .getQuickListData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const me = this.me();
          const users = (res?.response?.list ?? []).map((itm: any) => ({
            id: Number(itm?.id),
            name: Number(itm?.id) === me ? `${itm?.name} (me)` : itm?.name,
          }));
          if (me && !users.some((u: any) => u.id === me)) {
            users.unshift({ id: me, name: `${this.usersService.USER?.detail?.name || 'Me'} (me)` });
          }
          this.usersList.set(users);
        },
        error: (err: any) => this.toastr.showError(err?.message || 'Failed to load users'),
      });
  }
}
